import { cloneDeep, groupBy, throttle } from "lodash-es";
import { Doc, Op, Error as ShareDBError } from "sharedb";
import { TLDrawShapeProps, TLRecord, TLShape, TLStore } from "tldraw";

export const WHITEBOARD_TOOLBAR_WIDTH_PX = 342;
export const MIN_WHITEBOARD_WIDTH_PX = WHITEBOARD_TOOLBAR_WIDTH_PX + 8; // this is the width of the toolbar + some padding

export type TLShareDBOp = { p: string[]; oi?: TLRecord; od?: boolean };
export type ThrottledDoc = Doc & {
  submitImmediate: Doc["submitOp"];
};
export type ThrottledTLStore = TLStore & {
  mergeChanges: (put: TLRecord[], remove: TLRecord["id"][]) => void;
};

// For freehand drawings, reduce the number of points, so we don't send so much data.
// The resulting draw stroke might appear a bit smoother than the original, but not to an impactful degree.
function reducePointsForShape(op: TLShareDBOp) {
  const shape = op.oi as TLShape;
  if (shape?.type === "draw") {
    const opCopy = cloneDeep(op);
    const points = (shape.props as TLDrawShapeProps).segments[0].points;
    const filteredPoints = points.filter((_: unknown, index: number) => {
      // The more points there are in a segment, the safer it is to remove more points
      let reducePoints = 3;
      if (points.length < 100) {
        reducePoints = 1;
      } else if (points.length < 250) {
        reducePoints = 2;
      }
      return index % reducePoints === 0 || index === points.length - 1;
    });
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    opCopy.oi.props.segments[0].points = filteredPoints;
    return opCopy;
  }
  return op;
}

// To improve performance, we reduce the frequency and amount of data sent to ShareDB by throttling
// the number of ops that are submitted to ShareDB and reducing the number of points in freehand drawings.
export const throttleOutgoingShareDBOps = (doc: Doc, throttlePeriodMs: number) => {
  let changes: TLShareDBOp[] = [];
  const throttledDoc = doc as ThrottledDoc;
  // eslint-disable-next-line @typescript-eslint/unbound-method
  throttledDoc.submitImmediate = doc.submitOp;

  const throttledSubmit = throttle(
    () => {
      if (changes.length > 0) {
        const changesGroupedByShapeId = groupBy(changes, (change) => change.p[1]);
        const filteredChanges: TLShareDBOp[] = [];
        Object.values(changesGroupedByShapeId).forEach((changesForShapeId) => {
          // Always include the first change
          filteredChanges.push(reducePointsForShape(changesForShapeId[0]));
          // And the last change
          if (changesForShapeId.length > 1) {
            filteredChanges.push(
              reducePointsForShape(changesForShapeId[changesForShapeId.length - 1]),
            );
          }
        });

        throttledDoc.submitImmediate(filteredChanges, {}, (err: ShareDBError) => {
          if (err) {
            console.error("[throttleShareDBDoc] Error when submitting ops", err);
          }
        });
        changes = [];
      }
    },
    throttlePeriodMs,
    { leading: true, trailing: true },
  );

  throttledDoc.submitOp = (ops: Op[]) => {
    changes = changes.concat(ops as TLShareDBOp[]);
    throttledSubmit();
  };

  return throttledDoc;
};

// To improve performance, we reduce the frequency of changes to the TLStore by throttling
// the number of changes that come from ShareDB.
export const throttleIncomingShareDBOps = (store: TLStore, throttlePeriodMs: number) => {
  let toPut: TLRecord[] = [];
  let toRemove: TLRecord["id"][] = [];
  const throttledStore = store as ThrottledTLStore;

  const throttledMergeChanges = throttle(
    () => {
      throttledStore.mergeRemoteChanges(() => {
        if (toPut.length) {
          throttledStore.put(toPut);
        }
        if (toRemove.length) {
          throttledStore.remove(toRemove);
        }
      });
      toPut = [];
      toRemove = [];
    },
    throttlePeriodMs,
    { leading: true, trailing: true },
  );

  throttledStore.mergeChanges = (put: TLRecord[], remove: TLRecord["id"][]) => {
    toPut = toPut.concat(put);
    toRemove = toRemove.concat(remove);
    throttledMergeChanges();
  };

  return throttledStore;
};
